import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { getHashedLoomVideoIdFromUrl } from '@atlassian/analytics-data-privacy';

import { useLoomEntryPointVariant, useLoomRecorderEntryPoint } from '@confluence/loom-utils';
import type { FlagsStateContainer } from '@confluence/flags';
import { useSpaceId } from '@confluence/space-utils';

type openLoomRecorderOtherProps = {
	insertButtonText: string;
	analyticsAttributes?: {
		clickedSource?: string;
		insertSource?: string;
		insertContainerId?: string;
		additionalAttributes?: Record<string, string>;
	};
};
type openLoomRecorderArgs = {
	onSuccess: (url: string) => void;
	otherProps: openLoomRecorderOtherProps;
};

export const useLoomService = (flags: FlagsStateContainer, contentId: string) => {
	const entryPointVariant = useLoomEntryPointVariant();
	const isEntryPointEnabled = ['BETA', 'CO_USE', 'CROSS_JOIN'].includes(entryPointVariant);

	const { openLoomRecorder: localOpenLoom } = useLoomRecorderEntryPoint({
		flags,
		entryPointLocation: 'whiteboards',
		resetInsertFunctionOnUnmount: true,
	});
	const spaceId = useSpaceId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isAvailable = () => isEntryPointEnabled && !!localOpenLoom;
	const openLoomRecorder = ({ onSuccess, otherProps }: openLoomRecorderArgs) => {
		const onLoomInsert = (videoUrl: string) => {
			onSuccess(videoUrl);
			void getHashedLoomVideoIdFromUrl(videoUrl).then((hashedVideoId) => {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'insertVideo',
						actionSubject: 'loom',
						source: 'whiteboardSideBar',
						containerType: 'space',
						containerId: spaceId, // spaceKey can have UGC so use spaceId
						objectType: 'whiteboard',
						objectId: contentId,
						attributes: {
							video_id: hashedVideoId,
						},
					},
				}).fire();
			});
		};
		return localOpenLoom({ ...otherProps, onInsert: onLoomInsert });
	};

	return { openLoomRecorder, isAvailable };
};

export type loomService = ReturnType<typeof useLoomService>;
